import type { StepChangeData } from '@formkit/addons'
import { getNode } from '@formkit/core'

type UseFormKitMultiStep<T> = {
  /**
   * Go to the specified step.
   */
  goToStep: (id: T) => void

  /**
   * The currently active step, validated using the provided valid steps.
   */
  currentStep: ComputedRef<T>

  /**
   * The currently active step index.
   */
  currentStepIndex: ComputedRef<number>
}

/**
 * A helper composable to interact with multi step FormKit forms.
 */
export default function <T extends string>(
  validSteps: T[],
): UseFormKitMultiStep<T> {
  const currentStepQuery = useQueryString('step', validSteps[0])
  const { dataLayerPush } = useAnalytics()

  function isStepValid(step: string): step is T {
    return validSteps.includes(step as any)
  }

  function getMultiStepNode() {
    return getNode('steps')
  }

  async function goToStep(stepId: T) {
    const node = getMultiStepNode()
    if (node) {
      await node.settled
      node.calm()
      await node.settled
      node.goTo(stepId)
    }
  }

  onMounted(() => {
    const node = getMultiStepNode()
    if (!node) {
      console.error('Failed to locate multi step FormKit node!')
      return
    }

    node.on('mounted', function () {
      // Set initial active step.
      if (isStepValid(currentStepQuery.value)) {
        goToStep(currentStepQuery.value)
      }
    })

    if (currentStepQuery.value) {
      node.props.activeStep = currentStepQuery.value
    }

    node.props.beforeStepChange = function (ctx: StepChangeData) {
      currentStepQuery.value = ctx.targetStep.id
    }
  })

  const currentStep = computed(() => {
    if (isStepValid(currentStepQuery.value)) {
      return currentStepQuery.value
    }

    return validSteps[0]
  })

  const currentStepIndex = computed(() =>
    validSteps.findIndex((v) => v === currentStep.value),
  )

  function trackCurrentStep() {
    dataLayerPush({
      event: 'processStep',
      step: currentStepIndex.value,
    })
  }

  watch(currentStepIndex, trackCurrentStep)
  trackCurrentStep()

  return {
    goToStep,
    currentStep,
    currentStepIndex,
  }
}
